import { useEffect, useMemo, useRef } from 'react';
import { Warning, Check } from '@phosphor-icons/react';
import { InProgressIcon } from 'src/components/InProgressIcon';
import { SVG_SIZE_S } from 'src/constants';
import { useEditorState, useVisible } from 'src/hooks';
import classNames from 'classnames';
import { FadeTransition } from 'src/animations/FadeTransition';
import styles from './EditorAutoSaveLabel.module.scss';

export const EditorAutoSaveLabel = () => {
  const { isAutoSavingError, isAutoSavingLoading, isAutoSavingSuccess } =
    useEditorState();

  const nodeRef = useRef<HTMLDivElement | null>(null);

  const {
    isVisible: isLabelVisible,
    onVisibilityRemove: onLabelVisibilityRemove,
    onVisibilitySet: onLabelVisibilitySet,
  } = useVisible();

  useEffect(() => {
    if (isAutoSavingLoading === true) {
      onLabelVisibilitySet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSavingLoading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isAutoSavingLoading === false) {
      timeout = setTimeout(() => {
        onLabelVisibilityRemove();
      }, 2000);
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSavingLoading]);

  const IconComponent = useMemo(
    () =>
      isAutoSavingLoading ? (
        <InProgressIcon size={SVG_SIZE_S} />
      ) : isAutoSavingError ? (
        <Warning size={SVG_SIZE_S} />
      ) : (
        <Check size={SVG_SIZE_S} />
      ),
    [isAutoSavingLoading, isAutoSavingError],
  );

  const title = useMemo(() => {
    if (isAutoSavingLoading) {
      return 'Saving…';
    }

    if (isAutoSavingError) {
      return 'Error saving. Check if you are offline.';
    }

    return 'Saved';
  }, [isAutoSavingLoading, isAutoSavingError]);

  return (
    <FadeTransition inProp={isLabelVisible} nodeRef={nodeRef}>
      <div
        ref={nodeRef}
        className={classNames(styles.root, {
          [styles.error]: isAutoSavingError,
          [styles.success]: isAutoSavingSuccess,
        })}
      >
        {IconComponent}
        <span>{title}</span>
      </div>
    </FadeTransition>
  );
};
